<template>
  <div :class="avatarGroupClass">
    <component
      :is="SbAvatar"
      v-for="(element, index) of visibleAvatars"
      :key="index"
      v-bind="getAvatarAttrs(element)"
    />
    <div
      v-if="totalHiddenAvatars"
      class="sb-avatar-group__more"
      data-testid="sb-more-avatars-label"
    >
      {{ moreAvatarsLabel }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type VNode } from 'vue'
import type { AvatarSize } from '../Avatar/utils'
import type { AvatarProps } from '../Avatar/SbAvatar.vue'
import SbAvatar from '../Avatar/SbAvatar.vue'

export type AvatarGroupProps = {
  maxElements?: number
  size?: AvatarSize
}

const props = withDefaults(defineProps<AvatarGroupProps>(), {
  maxElements: 5,
  size: undefined,
})

const slots = defineSlots<{
  default(): VNode<AvatarProps>[]
}>()

const avatars = computed(() => {
  const defaultSlots = slots.default?.()
  const firstElement = defaultSlots?.[0]
  // we are assuming that the first element is a fragment
  // usually this happens when we are using the SbAvatar with v-for
  if (typeof firstElement?.type === 'symbol') {
    const children = firstElement.children

    // we are ignoring the type error because we expect that the children is a list of SbAvatar components
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return children as unknown as VNode<AvatarProps>[]
  }

  // just return the default slots, which is a list of SbAvatar components
  return defaultSlots as VNode<AvatarProps>[]
})

const totalAvatars = computed(() => avatars.value.length)

const visibleAvatars = computed(() => avatars.value.slice(0, props.maxElements))

const hiddenAvatars = computed(() => {
  const hiddenAvatarsIndex = props.maxElements - totalAvatars.value

  return hiddenAvatarsIndex < 0
    ? avatars.value.slice(hiddenAvatarsIndex)
    : []
})

const totalHiddenAvatars = computed(() => hiddenAvatars.value.length)

const avatarGroupClass = computed(() => {
  const sizeClass = props.size && `sb-avatar-group--${props.size}`

  return ['sb-avatar-group', sizeClass]
})

const moreAvatarsLabel = computed(() => `+${totalHiddenAvatars.value}`)

const getAvatarAttrs = (element: VNode<AvatarProps>) => {
  return {
    ...element.props,
    useTooltip: true,
    size: props.size,
  }
}
</script>
